import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Card, Select, Input, Button, Space, Typography, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import NodeInputHandler from '../common/NodeInputHandler';

const { Option } = Select;
const { Text } = Typography;

const ConditionNode = ({ id, data, isConnectable }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `Condition ${id}`);
  const [conditionGroups, setConditionGroups] = useState(data.conditionGroups || [{
    type: 'AND',
    conditions: [{ input: '', comparison: 'equals', value: '' }]
  }]);

  useEffect(() => {
    data.updateNodeData(id, { conditionGroups, nodeName });
  }, [conditionGroups, nodeName, id, data]);

  const handleConditionChange = (groupIndex, conditionIndex, field, value) => {
    const newGroups = [...conditionGroups];
    newGroups[groupIndex].conditions[conditionIndex][field] = value;
    setConditionGroups(newGroups);
  };

  const addCondition = (groupIndex, type) => {
    const newGroups = [...conditionGroups];
    if (type) {
      // Adding a new group
      newGroups.push({
        type,
        conditions: [{ input: '', comparison: 'equals', value: '' }]
      });
    } else {
      // Adding to existing group
      newGroups[groupIndex].conditions.push({ input: '', comparison: 'equals', value: '' });
    }
    setConditionGroups(newGroups);
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    const newGroups = [...conditionGroups];
    if (newGroups[groupIndex].conditions.length === 1 && conditionGroups.length === 1) {
      // Don't remove if it's the last condition in the only group
      return;
    }
    if (conditionIndex === undefined) {
      // Remove entire group
      newGroups.splice(groupIndex, 1);
    } else {
      // Remove specific condition
      newGroups[groupIndex].conditions.splice(conditionIndex, 1);
      if (newGroups[groupIndex].conditions.length === 0) {
        newGroups.splice(groupIndex, 1);
      }
    }
    setConditionGroups(newGroups);
  };

  const comparisonOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'endsWith', label: 'Ends with' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' }
  ];

  return (
    <Card 
      title="Condition"
      extra={      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>}
      style={{ width: 350, position: 'relative' }}
      bodyStyle={{ padding: '16px' }}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {conditionGroups.map((group, groupIndex) => (
        <div key={groupIndex} style={{ marginBottom: 16, padding: 8, border: '1px dashed #d9d9d9' }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>{group.type} Group</Text>
            {conditionGroups.length > 1 && (
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => removeCondition(groupIndex)}
                size="small"
                style={{ float: 'right' }}
              />
            )}
          </div>
          
          {group.conditions.map((condition, conditionIndex) => (
            <Space key={conditionIndex} direction="vertical" style={{ width: '100%', marginBottom: 8 }} size="small">
              <NodeInputHandler
                nodeId={id}
                onInsert={(tag) => handleConditionChange(groupIndex, conditionIndex, 'input', condition.input + tag)}
              />
              <Input
                style={{ width: '100%' }}
                placeholder="Input"
                value={condition.input}
                onChange={(e) => handleConditionChange(groupIndex, conditionIndex, 'input', e.target.value)}
                className="nodrag"
              />
              <Select
                style={{ width: '100%' }}
                value={condition.comparison}
                onChange={(value) => handleConditionChange(groupIndex, conditionIndex, 'comparison', value)}
                className="nodrag"
              >
                {comparisonOptions.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
              <Input
                style={{ width: '100%' }}
                placeholder="Value"
                value={condition.value}
                onChange={(e) => handleConditionChange(groupIndex, conditionIndex, 'value', e.target.value)}
                className="nodrag"
              />
              {group.conditions.length > 1 && (
                <Button 
                  type="link" 
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeCondition(groupIndex, conditionIndex)}
                  size="small"
                />
              )}
            </Space>
          ))}
          
          <Button
            type="dashed"
            onClick={() => addCondition(groupIndex)}
            style={{ width: '100%', marginTop: 8 }}
            icon={<PlusOutlined />}
          >
            Add Condition to {group.type} Group
          </Button>
        </div>
      ))}

      <Space style={{ width: '100%', justifyContent: 'center' }}>
        <Button
          onClick={() => addCondition(conditionGroups.length, 'AND')}
          icon={<PlusOutlined />}
        >
          Add AND Group
        </Button>
        <Button
          onClick={() => addCondition(conditionGroups.length, 'OR')}
          icon={<PlusOutlined />}
        >
          Add OR Group
        </Button>
      </Space>

      <div style={{ position: 'absolute', right: -8, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <Text style={{ marginRight: 5, color: '#52c41a', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>True</Text>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            style={{ top: 'auto', right: -4, background: '#52c41a', width: 12, height: 12 }}
            isConnectable={isConnectable}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Text style={{ marginRight: 5, color: '#f5222d', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>False</Text>
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            style={{ top: 'auto', right: -4, background: '#f5222d', width: 12, height: 12 }}
            isConnectable={isConnectable}
          />
        </div>
      </div>
    </Card>
  );
};

export default ConditionNode;